<script setup lang="ts">
import { StarIcon } from 'lucide-vue-next'
import { useUnratedCollects } from '~/composables/collect/useUnratedCollects'

const { t } = useI18n()
const router = useRouter()
const { isProducer, isAdmin } = storeToRefs(useAuthStore())

const { data: unratedCollectsData, isPending } = useUnratedCollects()
const unratedCount = computed(() => unratedCollectsData.value?.count ?? 0)

const STORAGE_KEY = 'unrated-collects-banner-dismissed'
const DISMISS_DURATION_DAYS = 10

const isDismissed = ref(false)

const dismissedData = computed(() => {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored) as { count: number, date: string }
  } catch {
    return null
  }
})

const shouldShowBanner = computed(() => {
  if (!isProducer.value || isAdmin.value) return false
  if (isPending.value) return false
  if (unratedCount.value === 0) return false
  if (isDismissed.value) return false

  const dismissed = dismissedData.value
  if (!dismissed) return true

  // Check if dismissed date is older than 10 days
  const dismissedDate = new Date(dismissed.date)
  const now = new Date()
  const daysDiff = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
  if (daysDiff >= DISMISS_DURATION_DAYS) return true

  // Show if current count is greater than dismissed count
  return unratedCount.value > dismissed.count
})

function dismissBanner() {
  const data = {
    count: unratedCount.value,
    date: new Date().toISOString(),
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  isDismissed.value = true
}

function navigateToCollects() {
  router.push('/collects?unrated=true')
}
</script>

<template>
  <div
    v-if="shouldShowBanner"
    class="card bg-primary text-primary-content"
  >
    <div class="card-body flex flex-col sm:flex-row sm:items-center gap-4 py-4 sm:py-6">
      <div class="flex items-center gap-3 flex-1 min-w-0">
        <StarIcon :size="24" class="flex-shrink-0" />
        <div class="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 min-w-0">
          <span class="text-sm sm:text-base font-bold">
            {{ t('collect.unrated_collects_banner_title', { count: unratedCount }) }}
          </span>
          <span class="text-xs sm:text-sm font-normal text-primary-content/70">
            {{ t('collect.unrated_collects_banner_subtitle') }}
          </span>
        </div>
      </div>
      <div class="inline-flex sm:flex sm:flex-row gap-3 sm:gap-4 flex-shrink-0">
        <EButton
          class="btn-secondary btn-outline px-4 min-w-[100px]"
          @click="dismissBanner"
        >
          {{ t('collect.unrated_collects_banner_skip') }}
        </EButton>
        <EButton
          class="btn-secondary px-4 min-w-[100px]"
          @click="navigateToCollects"
        >
          {{ t('collect.unrated_collects_banner_rate') }}
        </EButton>
      </div>
    </div>
  </div>
</template>
